import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Header from './components/Header';
import Footer from './components/Footer';
import SEO from './components/SEO';
import { TOOLS } from './constants';
import MobileNavBar from './components/MobileNavBar';

// Lazy load pages for code splitting and better FCP
const HomePage = lazy(() => import('./pages/HomePage'));
const ToolPageRoute = lazy(() => import('./pages/ToolPageRoute'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const DocsPage = lazy(() => import('./pages/DocsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Main App Component
const App = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleSearch = (query) => {
    if (query) {
      navigate(`/?q=${encodeURIComponent(query)}`);
    } else {
      navigate('/');
    }
  };

  const handleSelectCategory = (category) => {
    navigate(`/category/${encodeURIComponent(category)}`);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleNavigate = (route) => {
    navigate(`/${route}`);
  };

  const isToolPage = location.pathname.startsWith('/tool/');

  const normalizedPath = location.pathname.length > 1
    ? location.pathname.replace(/\/+$/, '')
    : location.pathname;

  // Get SEO data based on current route
  const getSEOData = () => {
    const path = normalizedPath;

    // Tool page
    if (path.startsWith('/tool/')) {
      const toolId = path.split('/tool/')[1];
      const tool = TOOLS.find(t => t.id === toolId);
      if (tool) {
        return {
          title: tool.name,
          description: tool.description,
          keywords: `${tool.name}, ${tool.category}, online tool, free tool`,
          schemaType: 'SoftwareApplication',
          tool: tool
        };
      }
    }

    // Category page
    if (path.startsWith('/category/')) {
      const categoryId = decodeURIComponent(path.split('/category/')[1]);
      return {
        title: `${categoryId}`,
        description: `Browse all ${categoryId} tools. Free online utilities for ${categoryId.toLowerCase()}.`,
        keywords: `${categoryId}, tools, online tools, free utilities`,
        category: categoryId
      };
    }

    // Blog detail page - handled by BlogDetailPage component's own Helmet
    if (path.startsWith('/blog/') && path !== '/blog') {
      return null; // Let BlogDetailPage handle its own SEO
    }

    // Other pages
    const seoMap = {
      '/': {
        title: '',
        description: 'Free all-in-one online toolbox. Convert text, test typing speed, count words, and more. All your online tools in one place.',
        keywords: 'online tools, free tools, text converter, typing speed test, word counter, case converter, online utilities'
      },
      '/about': {
        title: 'About Us',
        description: 'Learn more about runmytools - your free all-in-one online toolbox.',
        keywords: 'about, runmytools, online tools, free tools'
      },
      '/contact': {
        title: 'Contact Us',
        description: 'Get in touch with the runmytools team. We\'d love to hear from you!',
        keywords: 'contact, support, help, runmytools'
      },
      '/docs': {
        title: 'Documentation',
        description: 'Documentation and guides for using runmytools online tools.',
        keywords: 'documentation, guides, help, tutorials'
      },
      '/privacy': {
        title: 'Privacy Policy',
        description: 'Privacy policy for runmytools - how we handle your data.',
        keywords: 'privacy policy, data protection'
      },
      '/terms': {
        title: 'Terms of Service',
        description: 'Terms of service for using runmytools online tools.',
        keywords: 'terms of service, legal'
      },
      '/blog': {
        title: 'Blog',
        description: 'Latest articles and updates from runmytools.',
        keywords: 'blog, articles, updates, news',
        schemaType: 'Article'
      },
      '/login': {
        title: 'Login',
        description: 'Login to your runmytools account.',
        keywords: 'login, account, sign in'
      },
      '/signup': {
        title: 'Sign Up',
        description: 'Create a new runmytools account.',
        keywords: 'sign up, register, create account'
      }
    };

    return seoMap[path] || null;
  };

  const seoData = getSEOData();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {seoData && <SEO key={location.pathname} {...seoData} />}
      <Header
        currentView={location.pathname}
        onSearch={handleSearch}
        onBackToHome={handleBackToHome}
        onSelectCategory={handleSelectCategory}
        onNavigate={handleNavigate}
      />
      <Suspense fallback={
        <div className="flex-1 flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-10 h-10 animate-spin text-[#4048C5]" />
        </div>
      }>
        <Routes>
          <Route path="*" element={<NotFound />} />
          <Route path="/" element={<HomePage />} />

          <Route path="/tool/:id" element={<ToolPageRoute />} />
          <Route path="/category/:categoryId" element={<CategoryPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogDetailPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {!isToolPage && <Footer />}
      <MobileNavBar />
    </div>
  );
};

// export default memo(App, (prevProps, nextProps) => {
//   return prevProps.tool === nextProps.tool;
// });

export default App;
